import i18n from "../../../i18n";

// Copy owned by the shared overlays (search results, handover notes, confirm dialog, error and
// access screens). Existing labels keep going through their translate functions; only the new
// ones live here, so no catalogue key is added (DESIGN.md redesign rules).

const OV_COPY = {
  en: {
    groupGuest: "Guests", groupReservation: "Reservations", groupRoom: "Rooms", groupPage: "Pages", groupAction: "Actions", groupHotel: "Properties",
    groupCount: "{{shown}} of {{total}}",
    searchResults: "Search results",
    searchEmptyTitle: "Nothing matches “{{query}}”",
    searchEmptyText: "Try a guest name, reservation number, room number or e-mail.",
    searchPending: "Searching guest profiles…",
    searchGuestsFailed: "Guest profiles could not be searched. Only guests from loaded reservations and rooms are shown.",
    searchKeyMove: "move", searchKeyOpen: "open", searchKeyClose: "close",
    searchClear: "Clear search",
    searchOff: "Search is not available in this module yet",
    searchOffLabel: "Search", searchOffShort: "Not available",
    notesFilterAll: "All", notesFilterOpen: "To acknowledge",
    notesFilterLabel: "Show notes", notesReload: "Reload notes",
    notesAllAcknowledged: "You have acknowledged every active note.",
    notesShowAll: "Show all notes",
    accessHome: "Go to dashboard",
    errorReload: "Reload", errorRetry: "Try again", errorDetails: "Technical details",
    errorBackground: "Background error", errorDismiss: "Dismiss error",
    errorPosTitle: "This POS page could not be displayed",
    errorPosText: "A display error was caught. The rest of POS keeps working.",
  },
  de: {
    groupGuest: "Gäste", groupReservation: "Reservierungen", groupRoom: "Zimmer", groupPage: "Seiten", groupAction: "Aktionen", groupHotel: "Hotels",
    groupCount: "{{shown}} von {{total}}",
    searchResults: "Suchergebnisse",
    searchEmptyTitle: "Keine Treffer für „{{query}}“",
    searchEmptyText: "Versuchen Sie einen Gastnamen, eine Reservierungsnummer, Zimmernummer oder E-Mail.",
    searchPending: "Gästeprofile werden durchsucht…",
    searchGuestsFailed: "Gästeprofile konnten nicht durchsucht werden. Es werden nur Gäste aus geladenen Reservierungen und Zimmern angezeigt.",
    searchKeyMove: "bewegen", searchKeyOpen: "öffnen", searchKeyClose: "schließen",
    searchClear: "Suche leeren",
    searchOff: "Die Suche ist in diesem Modul noch nicht verfügbar",
    searchOffLabel: "Suchen", searchOffShort: "Nicht verfügbar",
    notesFilterAll: "Alle", notesFilterOpen: "Zu bestätigen",
    notesFilterLabel: "Notizen anzeigen", notesReload: "Notizen neu laden",
    notesAllAcknowledged: "Sie haben alle aktiven Notizen bestätigt.",
    notesShowAll: "Alle Notizen anzeigen",
    accessHome: "Zum Dashboard",
    errorReload: "Neu laden", errorRetry: "Erneut versuchen", errorDetails: "Technische Details",
    errorBackground: "Hintergrundfehler", errorDismiss: "Fehler schließen",
    errorPosTitle: "Diese POS-Seite konnte nicht angezeigt werden",
    errorPosText: "Ein Anzeigefehler wurde abgefangen. Der Rest des POS funktioniert weiter.",
  },
  pl: {
    groupGuest: "Goście", groupReservation: "Rezerwacje", groupRoom: "Pokoje", groupPage: "Strony", groupAction: "Akcje", groupHotel: "Hotele",
    groupCount: "{{shown}} z {{total}}",
    searchResults: "Wyniki wyszukiwania",
    searchEmptyTitle: "Brak wyników dla „{{query}}”",
    searchEmptyText: "Spróbuj nazwiska gościa, numeru rezerwacji, numeru pokoju lub e-maila.",
    searchPending: "Przeszukiwanie profili gości…",
    searchGuestsFailed: "Nie udało się przeszukać profili gości. Widoczni są tylko goście z wczytanych rezerwacji i pokoi.",
    searchKeyMove: "wybór", searchKeyOpen: "otwórz", searchKeyClose: "zamknij",
    searchClear: "Wyczyść wyszukiwanie",
    searchOff: "Wyszukiwanie nie jest jeszcze dostępne w tym module",
    searchOffLabel: "Szukaj", searchOffShort: "Niedostępne",
    notesFilterAll: "Wszystkie", notesFilterOpen: "Do potwierdzenia",
    notesFilterLabel: "Pokaż notatki", notesReload: "Odśwież notatki",
    notesAllAcknowledged: "Potwierdzono wszystkie aktywne notatki.",
    notesShowAll: "Pokaż wszystkie notatki",
    accessHome: "Przejdź do pulpitu",
    errorReload: "Odśwież", errorRetry: "Spróbuj ponownie", errorDetails: "Szczegóły techniczne",
    errorBackground: "Błąd w tle", errorDismiss: "Zamknij błąd",
    errorPosTitle: "Nie można wyświetlić tej strony POS",
    errorPosText: "Przechwycono błąd wyświetlania. Reszta POS działa dalej.",
  },
};

export type HosOverlayTextKey = keyof typeof OV_COPY.en;

export function ovText(key: HosOverlayTextKey, values: Record<string, string | number> = {}): string {
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? "en").slice(0, 2) as keyof typeof OV_COPY;
  const text: string = (OV_COPY[lang] ?? OV_COPY.en)[key];
  return text.replace(/\{\{(\w+)\}\}/g, (_, name: string) => String(values[name] ?? ""));
}

/** "Ctrl K" on Windows/Linux, "⌘K" on Apple keyboards. */
export function searchShortcutLabel(): string {
  if (typeof navigator === "undefined") return "Ctrl K";
  return /Mac|iPhone|iPad/i.test(navigator.platform || navigator.userAgent) ? "⌘K" : "Ctrl K";
}

// Same folding as utils/search.ts (diacritics, ł/ß/æ…, and the German ä→ae variant), applied per
// character so a match can be mapped back to the original text for highlighting.
const BASE_FOLD: Record<string, string> = { "ł": "l", "Ł": "l", "đ": "d", "Đ": "d", "ð": "d", "Ð": "d", "þ": "th", "Þ": "th", "æ": "ae", "Æ": "ae", "œ": "oe", "Œ": "oe", "ø": "o", "Ø": "o", "ß": "ss" };
const GERMAN_FOLD: Record<string, string> = { ...BASE_FOLD, "ä": "ae", "Ä": "ae", "ö": "oe", "Ö": "oe", "ü": "ue", "Ü": "ue" };

function foldChar(character: string, map: Record<string, string>) {
  const mapped = map[character] ?? character;
  const folded = mapped.normalize("NFKD").replace(/[̀-ͯ]/g, "").toLowerCase();
  return /^[\p{L}\p{N}]+$/u.test(folded) ? folded : " ";
}

function foldTokens(query: string) {
  return query
    .normalize("NFKD").replace(/[̀-ͯ]/g, "").toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);
}

/** Ranges [start, end) of `text` that match any word of `query`, merged and sorted. */
export function highlightRanges(text: string, query: string): Array<[number, number]> {
  const tokens = foldTokens(query);
  if (!text || !tokens.length) return [];
  const hits: Array<[number, number]> = [];
  for (const map of [BASE_FOLD, GERMAN_FOLD]) {
    // Every folded character remembers the [start, end) of the original character it came from.
    let folded = "";
    const starts: number[] = [];
    const ends: number[] = [];
    let offset = 0;
    for (const character of Array.from(text)) {
      const piece = foldChar(character, map);
      folded += piece;
      for (let index = 0; index < piece.length; index += 1) { starts.push(offset); ends.push(offset + character.length); }
      offset += character.length;
    }
    for (const token of tokens) {
      let from = folded.indexOf(token);
      while (from !== -1) {
        hits.push([starts[from], ends[from + token.length - 1]]);
        from = folded.indexOf(token, from + token.length);
      }
    }
  }
  hits.sort((left, right) => left[0] - right[0]);
  const merged: Array<[number, number]> = [];
  for (const [start, end] of hits) {
    const last = merged[merged.length - 1];
    if (last && start <= last[1]) last[1] = Math.max(last[1], end);
    else merged.push([start, end]);
  }
  return merged;
}
